// Form validation utilities

/**
 * Validate email address
 * Must be in format: user@example.com
 */
export const validateEmail = (email) => {
  if (!email || email.trim() === '') {
    return { isValid: false, error: 'Email address is required' };
  }
  
  // Enhanced email regex for better validation
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  
  if (!emailRegex.test(email)) {
    return { isValid: false, error: 'Please enter a valid email address (e.g., user@example.com)' };
  }
  
  return { isValid: true };
};

/**
 * Validate phone number with country code
 * Format: Country code + 10 digits
 * Example: +1 1234567890
 */
export const validatePhone = (phone, countryCode) => {
  if (!phone || phone.trim() === '') {
    return { isValid: false, error: 'Phone number is required' };
  }
  
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Check if contains only digits (no alphabets)
  if (!/^\d+$/.test(cleaned)) {
    return { isValid: false, error: 'Phone number must contain only digits' };
  }
  
  // Must be exactly 10 digits
  if (cleaned.length !== 10) {
    return { isValid: false, error: 'Phone number must be exactly 10 digits (e.g., 1234567890)' };
  }
  
  return { isValid: true };
};

/**
 * Validate phone number (10 digits only - for use with separate country code)
 * Returns boolean for simple checks
 */
export const validatePhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return false;
  
  // Remove all non-digit characters
  const cleaned = phoneNumber.toString().replace(/\D/g, '');
  
  // Check if it's exactly 10 digits and contains only numbers
  return /^\d{10}$/.test(cleaned);
};

/**
 * Restrict phone input to numbers only
 * Prevents alphabets and special characters (except during typing)
 */
export const sanitizePhoneInput = (value) => {
  // Remove all non-digit characters except the first + if present
  return value.replace(/[^\d]/g, '');
};

/**
 * Format phone number for display
 * Formats 10 digits into XXX-XXX-XXXX
 */
export const formatPhoneNumber = (phoneNumber) => {
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  
  return phoneNumber;
};

export const validateName = (name) => {
  return name && name.trim().length >= 2;
};

export const validateDescription = (description) => {
  return description && description.trim().length >= 10;
};

export const validateForm = (formData) => {
  const errors = {};

  if (!formData.name || !validateName(formData.name)) {
    errors.name = 'Name must be at least 2 characters long';
  }

  const emailValidation = validateEmail(formData.email);
  if (!emailValidation.isValid) {
    errors.email = emailValidation.error;
  }

  const phoneValidation = validatePhone(formData.phone, formData.countryCode);
  if (!phoneValidation.isValid) {
    errors.phone = phoneValidation.error;
  }

  if (!formData.description || !validateDescription(formData.description)) {
    errors.description = 'Description must be at least 10 characters long';
  }

  return errors;
};

